// src/oura/overview/NotesCard.tsx
//
// "Past notes to client": the two newest weekly notes, with who wrote them and
// when, so whoever writes this week's can see what was already said. Notes are
// written on the Client tab; this card only reads.

import { NotebookPenIcon } from "lucide-react";
import type { ClientNote } from "@service/aeo/clientReview";
import type { Load } from "../hooks/useOverviewData";
import { cn } from "../lib/cn";
import { dayDate, joinDotted } from "../lib/oura";
import { ArrowLink, FoldCard, SURFACE, TYPE } from "@oura/ui";
import { staffHref } from "./model";

const NOTES_SHOWN = 2;

export function NotesCard({
    businessId,
    notes,
}: {
    businessId: string;
    notes: Load<ClientNote[]>;
}) {
    return (
        <FoldCard
            id="notes"
            title="Past notes to client"
            icon={{ icon: NotebookPenIcon, area: "people" }}
            right={<ArrowLink to={staffHref(businessId, { view: "reviews", compose: "note" })}>Write this week&rsquo;s</ArrowLink>}
        >
            {notes.status === "loading" ? (
                <div aria-hidden className="space-y-2 py-2">
                    <span className="block h-3 w-full animate-pulse rounded bg-gray-100" />
                    <span className="block h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                </div>
            ) : notes.status === "ready" && notes.value.length > 0 ? (
                <div>
                    {notes.value.slice(0, NOTES_SHOWN).map((n) => (
                        <article key={n.id} className={cn("border-t py-2.5 first:border-t-0 first:pt-0", SURFACE.hairline)}>
                            <p className={cn(TYPE.tc, "m-0 whitespace-pre-line text-gray-600")}>{n.body}</p>
                            <p className={cn(TYPE.tm, "m-0 mt-0.5 text-gray-400")}>{joinDotted(n.author, dayDate(n.date))}</p>
                        </article>
                    ))}
                </div>
            ) : (
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                    {notes.status === "failed"
                        ? "The notes could not be read."
                        : "No notes yet. The client sees the newest on Home."}
                </p>
            )}
        </FoldCard>
    );
}
